import { SubscriptionUpdatedEvent, ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/utilities';

/*
 * Fréquence de livraison : elle suit le pack, sans choix pour le client.
 *
 * pack 1 -> livraison tous les mois
 * pack 2 -> tous les 2 mois
 * pack 3 -> tous les 3 mois
 *
 * Les selling plans Kaching sont reconnus par leur nom :
 * "Pack 2 mois · Livraison tous les 2 mois"
 * "Pack 3 mois · Livraison tous les 3 mois"
 * Les anciens plans "1 mois" / "2 mois" / "3 mois"
 * restent compris comme pack N livré tous les N mois.
 * Le menu de fréquence n'apparaît que s'il reste plusieurs choix.
 */
const FREQUENCY_MENU_STYLE_ID = 'glx-frequency-menu-style';

const FREQUENCY_MENU_CSS = `
  .subscription__frequency {
    position: relative;
    margin-top: 12px;
  }

  .subscription__frequency-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
    padding: 11px 14px;
    border: 0;
    border-radius: 8px;
    background: var(--glx-active-color, var(--subscription-card-selected-border, #1e88e5));
    color: #fff;
    font: inherit;
    font-size: 14px;
    line-height: 1.2;
    text-align: left;
    cursor: pointer;
  }

  .subscription__frequency-toggle strong {
    font-weight: 700;
  }

  .subscription__frequency-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: #fff;
    color: var(--glx-active-color, var(--subscription-card-selected-border, #1e88e5));
    animation: glx-frequency-nudge 1.4s ease-in-out infinite;
  }

  .subscription__frequency-icon svg {
    width: 16px;
    height: 16px;
    transition: transform 0.2s ease;
  }

  .subscription__frequency--open .subscription__frequency-icon {
    animation: none;
  }

  .subscription__frequency--open .subscription__frequency-icon svg {
    transform: rotate(180deg);
  }

  @keyframes glx-frequency-nudge {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(3px); }
  }

  @media (prefers-reduced-motion: reduce) {
    .subscription__frequency-icon {
      animation: none;
    }
  }

  .subscription__frequency-list {
    display: none;
    position: absolute;
    z-index: 20;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    margin: 0;
    padding: 0;
    list-style: none;
    border: 1px solid #d0d0d0;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
    overflow: hidden;
  }

  .subscription__frequency--open .subscription__frequency-list {
    display: block;
  }

  .subscription__frequency-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 14px;
    color: #000;
    font-size: 14px;
    line-height: 1.2;
    cursor: pointer;
  }

  .subscription__frequency-item:hover,
  .subscription__frequency-item:focus-visible,
  .subscription__frequency-item--selected {
    background: #f4f4f4;
    outline: none;
  }

  .subscription__frequency-saving {
    color: #777;
    font-size: 13px;
    white-space: nowrap;
  }
`;

const CHEVRON_SVG = `
  <span class="subscription__frequency-icon" aria-hidden="true">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
  </span>
`;

class SubscriptionComponent extends HTMLElement {
  constructor() {
    super();

    this.sellingPlanInput = null;

    this.oneTimeOption = null;
    this.subscriptionOption = null;

    this.oneTimeCard = null;
    this.subscriptionCard = null;

    this.frequencySelect = null;

    this.oneTimePriceContainer = null;
    this.subscriptionPriceContainer = null;

    this.savingBadge = null;

    this.productForm = null;

    /*
     * Fréquence choisie par le client (en mois)
     * et fréquence réellement appliquée au plan.
     */
    this.selectedFrequency = 1;
    this.currentFrequency = 1;

    this.handleOneTimeChange = () => {
      this.#onOptionChange('one_time');
    };

    this.handleSubscriptionChange = () => {
      this.#onOptionChange('subscription');
    };

    this.handleQuantityBreakUpdate = () => {
      this.#onQuantityBreakUpdate();
    };

    this.handleVariantUpdate = (event) => {
      this.#onVariantUpdate(event);
    };

    this.handleFrequencyClick = (event) => {
      this.#onFrequencyClick(event);
    };

    this.handleFrequencyKeydown = (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;

      if (!event.target.closest('.subscription__frequency-item')) return;

      this.#onFrequencyClick(event);
    };

    this.handleDocumentClick = (event) => {
      if (!this.contains(event.target)) {
        this.#setFrequencyMenuOpen(false);
      }
    };

    /*
     * L'app Kaching réécrit selling_plan avec son
     * plan par défaut (le 1er du groupe) : on remet
     * le plan choisi juste avant l'ajout au panier.
     */
    this.handleAddToCartCapture = (event) => {
      const form = this.sellingPlanInput?.form;

      if (!form) return;

      if (
        event.type === 'click' &&
        event.target.closest?.('[type="submit"], button[name="add"]')?.form !== form
      ) {
        return;
      }

      if (event.type === 'submit' && event.target !== form) return;

      this.#restoreSellingPlan();
    };
  }

  connectedCallback() {
    this.#injectFrequencyMenuStyle();

    this.#initDomReferences();
    this.#attachListeners();

    /*
     * Listeners délégués : ils survivent au
     * remplacement du innerHTML au changement de saveur.
     */
    this.addEventListener('click', this.handleFrequencyClick);
    this.addEventListener('keydown', this.handleFrequencyKeydown);
    document.addEventListener('click', this.handleDocumentClick);

    document.addEventListener('click', this.handleAddToCartCapture, true);
    document.addEventListener('submit', this.handleAddToCartCapture, true);

    document.addEventListener(
      ThemeEvents.variantUpdated,
      this.handleVariantUpdate
    );

    document.addEventListener(
      ThemeEvents.quantityBreaksUpdated,
      this.handleQuantityBreakUpdate
    );

    /*
    * D'abord déterminer achat unique / abonnement.
    */
    this.#initDefaultSelection();

    /*
    * Ensuite seulement synchroniser le selling plan.
    */
    this.#syncPlanWithQuantity();

    this.#updateDeliveryText();

    this.#updateDisplayedPrices();
  }

  disconnectedCallback() {
    this.#detachListeners();

    this.removeEventListener('click', this.handleFrequencyClick);
    this.removeEventListener('keydown', this.handleFrequencyKeydown);
    document.removeEventListener('click', this.handleDocumentClick);

    document.removeEventListener('click', this.handleAddToCartCapture, true);
    document.removeEventListener('submit', this.handleAddToCartCapture, true);

    document.removeEventListener(
      ThemeEvents.variantUpdated,
      this.handleVariantUpdate
    );

    document.removeEventListener(
      ThemeEvents.quantityBreaksUpdated,
      this.handleQuantityBreakUpdate
    );
  }

  #updateDeliveryText() {
    if (!this.deliveryText) return;

    const quantity =
      this.#getSelectedQuantity();

    const every =
      this.currentFrequency <= 1
        ? 'tous les mois'
        : `tous les ${this.currentFrequency} mois`;

    if (quantity <= 1) {
      this.deliveryText.textContent =
        `Recevez vos gummies ${every}`;
    } else {
      this.deliveryText.textContent =
        `Recevez ${quantity} sachets ${every}`;
    }
  }

  #initDomReferences() {
    this.sellingPlanInput = this.querySelector(
      '[data-ref="selling-plan-input"]'
    );

    this.oneTimeOption = this.querySelector(
      '[data-ref="one-time-option"]'
    );

    this.subscriptionOption = this.querySelector(
      '[data-ref="subscription-option"]'
    );

    this.oneTimeCard = this.querySelector(
      '[data-ref="one-time-card"]'
    );

    this.subscriptionCard = this.querySelector(
      '[data-ref="subscription-card"]'
    );

    this.frequencySelect = this.querySelector(
      '[data-ref="frequency-select"]'
    );

    this.oneTimePriceContainer = this.querySelector(
      '[data-ref="one-time-price"]'
    );

    this.subscriptionPriceContainer = this.querySelector(
      '[data-ref="subscription-price"]'
    );

    this.savingBadge = this.querySelector(
      '[data-ref="subscription-saving-badge"]'
    );
    this.deliveryText =
      this.querySelector(
        '[data-ref="subscription-delivery-text"]'
      );
    this.productForm =
      this.closest('[data-ref="product-section-content"]')
        ?.querySelector('[data-ref="product-form"]') ||
      this.closest('[data-ref="popup-content"]')
        ?.querySelector('[data-ref="product-form"]');
  }

  #attachListeners() {
    this.oneTimeOption?.addEventListener(
      'change',
      this.handleOneTimeChange
    );

    this.subscriptionOption?.addEventListener(
      'change',
      this.handleSubscriptionChange
    );
  }

  #detachListeners() {
    this.oneTimeOption?.removeEventListener(
      'change',
      this.handleOneTimeChange
    );

    this.subscriptionOption?.removeEventListener(
      'change',
      this.handleSubscriptionChange
    );
  }

  /* =========================================
     CHANGEMENT DE PRODUIT / SAVEUR
  ========================================= */

  #onVariantUpdate(event) {
    if (!event.detail?.data?.html) return;

    const newSubscription =
      event.detail.data.html.querySelector(
        'subscription-component'
      );

    const hasSubscriptionContent =
      newSubscription?.querySelector(
        '[data-ref="selling-plan-input"]'
      );

    /*
     * Nouveau produit sans abonnement
     */
    if (!hasSubscriptionContent) {
      this.style.display = 'none';

      this.#updateSellingPlanInput('');

      console.warn(
        '[GLX Subscription] Aucun selling plan disponible sur le nouveau produit'
      );

      return;
    }

    /*
     * On mémorise le choix actuel
     * abonnement / achat unique
     */
    const wasSubscriptionSelected =
      this.isSubscriptionSelected();

    /*
     * Retrait des listeners de l'ancien DOM
     */
    this.#detachListeners();

    /*
     * On récupère tout le contenu du nouveau produit :
     * - nouveaux selling plan IDs
     * - nouveaux prix
     * - nouvelles allocations
     */
    this.innerHTML =
      newSubscription.innerHTML;

    this.style.display = '';

    /*
     * Réinitialisation des références
     */
    this.#initDomReferences();
    /*
     * Nouveaux listeners
     */
    this.#attachListeners();

    /*
     * Important :
     * resynchroniser avec le pack sélectionné
     * (la fréquence choisie est conservée)
     */
    this.#syncPlanWithQuantity();
    this.#updateDeliveryText();

    /*
     * Conserver abonnement / achat unique
     */
    if (
      wasSubscriptionSelected &&
      this.subscriptionOption
    ) {
      this.subscriptionOption.checked = true;

      this.#onOptionChange(
        'subscription',
        false
      );
    } else if (this.oneTimeOption) {
      this.oneTimeOption.checked = true;

      this.#onOptionChange(
        'one_time',
        false
      );
    }

    this.#updateDisplayedPrices();

    this.dispatchEvent(
      new SubscriptionUpdatedEvent()
    );
  }

  /* =========================================
     CHANGEMENT DE PACK
  ========================================= */

  #onQuantityBreakUpdate() {

    this.#syncPlanWithQuantity();

    this.#updateDeliveryText();

    this.#updateDisplayedPrices();

    this.dispatchEvent(
      new SubscriptionUpdatedEvent()
    );
  }

  /* =========================================
     PACK + FRÉQUENCE -> SELLING PLAN
  ========================================= */

  /*
   * Lit le pack et la fréquence (en mois) dans le nom du plan.
   * Retourne null si le plan ne suit aucune des conventions.
   */
  #getPlanMeta(option) {
    const name =
      (
        option.dataset.planName ||
        option.textContent ||
        ''
      )
        .trim()
        .toLowerCase();

    const packMatch =
      name.match(/pack\s*(\d+)/);

    if (packMatch) {
      const pack =
        parseInt(packMatch[1], 10);

      const frequencyMatch =
        name.match(/tous les\s*(\d+)?\s*mois/);

      const frequency =
        frequencyMatch
          ? parseInt(frequencyMatch[1] || '1', 10)
          : pack;

      return { pack, frequency };
    }

    /*
     * Anciens plans : "1 mois", "2 mois", "3 mois"
     */
    const legacyMatch =
      name.match(/^(\d+)\s*mois$/);

    if (legacyMatch) {
      const months =
        parseInt(legacyMatch[1], 10);

      return {
        pack: months,
        frequency: months
      };
    }

    return null;
  }

  /*
   * Plan disponible pour un pack : uniquement la livraison
   * au rythme du pack (pack N = tous les N mois).
   */
  #getPlansForPack(pack) {
    if (!this.frequencySelect) {
      return [];
    }

    const plansByFrequency = new Map();

    Array.from(
      this.frequencySelect.options
    ).forEach((option) => {
      const meta =
        this.#getPlanMeta(option);

      if (
        !meta ||
        meta.pack !== pack ||
        meta.frequency !== pack
      ) {
        return;
      }

      if (!plansByFrequency.has(meta.frequency)) {
        plansByFrequency.set(meta.frequency, option);
      }
    });

    return Array.from(
      plansByFrequency.entries()
    )
      .sort((a, b) => a[0] - b[0])
      .map(([frequency, option]) => ({
        frequency,
        option
      }));
  }

  #syncPlanWithQuantity() {
    if (!this.frequencySelect) {
      console.warn(
        '[GLX Subscription] frequencySelect introuvable'
      );

      return;
    }

    const quantity =
      this.#getSelectedQuantity();

    const plans =
      this.#getPlansForPack(quantity);

    /*
     * Garder la fréquence choisie si elle existe
     * pour ce pack, sinon prendre le plan du pack.
     */
    const matchingPlan =
      plans.find((plan) => {
        return plan.frequency === this.selectedFrequency;
      }) || plans[0];

    let matchingOption =
      matchingPlan?.option;

    /*
     * Sécurité :
     * aucun plan reconnu pour ce pack,
     * chercher le premier nombre dans le nom,
     * sinon prendre le premier plan.
     */
    if (!matchingOption) {
      matchingOption =
        Array.from(
          this.frequencySelect.options
        ).find((option) => {
          const name =
            (
              option.dataset.planName ||
              option.textContent ||
              ''
            )
              .trim()
              .toLowerCase();

          const match =
            name.match(/\d+/);

          if (!match) return false;

          return (
            parseInt(
              match[0],
              10
            ) === quantity
          );
        });
    }

    if (!matchingOption) {
      matchingOption =
        this.frequencySelect.options[0];
    }

    if (!matchingOption) {
      console.warn(
        '[GLX Subscription] Aucun selling plan trouvé'
      );

      return;
    }

    this.currentFrequency =
      matchingPlan
        ? matchingPlan.frequency
        : quantity;

    this.frequencySelect.value =
      matchingOption.value;

    /*
    * Le select interne pointe toujours vers
    * le bon plan pack + fréquence.
    *
    * MAIS le vrai input Shopify ne doit contenir
    * un selling_plan QUE si l'abonnement est coché.
    */
    if (this.isSubscriptionSelected()) {
      this.#updateSellingPlanInput(
        matchingOption.value
      );
    } else {
      this.#updateSellingPlanInput('');
    }

    console.log(
      '[GLX Subscription] Plan sélectionné :',
      {
        pack: quantity,
        frequency: this.currentFrequency,
        name:
          matchingOption.dataset.planName ||
          matchingOption.textContent.trim(),
        sellingPlanSent:
          this.sellingPlanInput?.value || ''
      }
    );

    this.#renderFrequencyMenu(
      plans,
      matchingPlan
    );

    this.#updateSavingBadge();
  }

  #getSelectedQuantity() {
    const quantityBreaks =
      this.productForm?.querySelector(
        '[data-ref="quantity-breaks"]'
      );

    if (quantityBreaks) {
      const selected =
        quantityBreaks
          .getSelectedQuantity?.();

      const quantity =
        parseInt(
          selected?.dataset?.quantity,
          10
        );

      if (quantity > 0) {
        return quantity;
      }
    }

    /*
     * Pages packs (Duo, Trio) : pas de sélecteur,
     * le nombre de sachets vient du réglage du bloc.
     */
    const packQuantity =
      parseInt(this.dataset.packQuantity, 10);

    if (packQuantity > 0) {
      return packQuantity;
    }

    return 1;
  }

  /* =========================================
     MENU FRÉQUENCE
  ========================================= */

  #injectFrequencyMenuStyle() {
    if (document.getElementById(FREQUENCY_MENU_STYLE_ID)) {
      return;
    }

    const style =
      document.createElement('style');

    style.id = FREQUENCY_MENU_STYLE_ID;
    style.textContent = FREQUENCY_MENU_CSS;

    document.head.appendChild(style);
  }

  #formatFrequency(frequency) {
    return frequency <= 1
      ? 'tous les mois'
      : `tous les ${frequency} mois`;
  }

  #getPlanSavingPercentage(option) {
    const price =
      parseFloat(option.dataset.allocationPrice);

    const compareAtPrice =
      parseFloat(option.dataset.allocationComparePrice);

    if (
      Number.isFinite(price) &&
      Number.isFinite(compareAtPrice) &&
      compareAtPrice > price
    ) {
      return Math.round(
        (compareAtPrice - price) /
          compareAtPrice *
          100
      );
    }

    if (option.dataset.adjustmentType === 'percentage') {
      return Math.round(
        parseFloat(option.dataset.adjustmentValue) || 0
      );
    }

    return 0;
  }

  #renderFrequencyMenu(plans, currentPlan) {
    if (!this.subscriptionCard) return;

    let menu =
      this.querySelector('[data-ref="frequency-menu"]');

    /*
     * Un seul rythme possible pour le pack :
     * pas de menu, le texte de livraison suffit.
     */
    if (plans.length <= 1 || !currentPlan) {
      menu?.remove();
      return;
    }

    if (!menu) {
      menu = document.createElement('div');
      menu.className = 'subscription__frequency';
      menu.dataset.ref = 'frequency-menu';

      const deliveryInfo =
        this.subscriptionCard.querySelector(
          '[data-ref="subscription-delivery-info"]'
        );

      if (deliveryInfo) {
        deliveryInfo.before(menu);
      } else {
        this.subscriptionCard.append(menu);
      }
    }

    const isOpen =
      menu.classList.contains('subscription__frequency--open');

    const items =
      plans.map((plan) => {
        const isSelected =
          plan === currentPlan;

        const saving =
          this.#getPlanSavingPercentage(plan.option);

        return `
          <li
            class="subscription__frequency-item${isSelected ? ' subscription__frequency-item--selected' : ''}"
            role="option"
            tabindex="0"
            aria-selected="${isSelected}"
            data-frequency="${plan.frequency}"
          >
            <span>Livraison ${this.#formatFrequency(plan.frequency)}</span>
            ${saving > 0 ? `<span class="subscription__frequency-saving">économisez ${saving}%</span>` : ''}
          </li>
        `;
      }).join('');

    menu.innerHTML = `
      <button
        type="button"
        class="subscription__frequency-toggle"
        data-ref="frequency-toggle"
        aria-haspopup="listbox"
        aria-expanded="${isOpen}"
      >
        <span>Livraison <strong>${this.#formatFrequency(currentPlan.frequency)}</strong></span>
        ${CHEVRON_SVG}
      </button>

      <ul class="subscription__frequency-list" role="listbox">
        ${items}
      </ul>
    `;
  }

  #setFrequencyMenuOpen(open) {
    const menu =
      this.querySelector('[data-ref="frequency-menu"]');

    if (!menu) return;

    menu.classList.toggle('subscription__frequency--open', open);

    menu.querySelector('[data-ref="frequency-toggle"]')
      ?.setAttribute('aria-expanded', String(open));
  }

  #onFrequencyClick(event) {
    const toggle =
      event.target.closest('[data-ref="frequency-toggle"]');

    const item =
      event.target.closest('.subscription__frequency-item');

    if (!toggle && !item) return;

    /*
     * Le menu est dans le <label> de la carte abonnement :
     * on bloque le comportement natif du label.
     */
    event.preventDefault();

    if (toggle) {
      const menu =
        this.querySelector('[data-ref="frequency-menu"]');

      this.#setFrequencyMenuOpen(
        !menu?.classList.contains('subscription__frequency--open')
      );

      return;
    }

    this.selectedFrequency =
      parseInt(item.dataset.frequency, 10) || 1;

    this.#setFrequencyMenuOpen(false);

    /*
     * Choisir une fréquence = choisir l'abonnement.
     */
    if (
      !this.isSubscriptionSelected() &&
      this.subscriptionOption
    ) {
      this.subscriptionOption.checked = true;

      this.#onOptionChange('subscription');
    } else {
      this.#syncPlanWithQuantity();

      this.#updateDisplayedPrices();

      this.dispatchEvent(
        new SubscriptionUpdatedEvent()
      );
    }

    this.#updateDeliveryText();
  }

  /* =========================================
     ABONNEMENT / ACHAT UNIQUE
  ========================================= */

  #initDefaultSelection() {
    const requiresSellingPlan =
      this.dataset.requiresSellingPlan ===
      'true';

    /*
     * Produit abonnement obligatoire
     */
    if (
      requiresSellingPlan &&
      this.subscriptionOption
    ) {
      this.subscriptionOption.checked = true;

      this.#onOptionChange(
        'subscription',
        false
      );

      return;
    }

    const defaultSelected =
      this.dataset.defaultSelected ||
      'subscription';

    if (
      defaultSelected === 'subscription' &&
      this.subscriptionOption
    ) {
      this.subscriptionOption.checked = true;

      this.#onOptionChange(
        'subscription',
        false
      );
    } else if (this.oneTimeOption) {
      this.oneTimeOption.checked = true;

      this.#onOptionChange(
        'one_time',
        false
      );
    }
  }

  #onOptionChange(
    optionType,
    notify = true
  ) {
    this.#updateSelectedCard(
      optionType
    );

    if (
      optionType === 'subscription'
    ) {
      /*
       * Vérifier le bon plan avant
       * d'alimenter selling_plan.
       */
      this.#syncPlanWithQuantity();

      this.#updateSellingPlanInput(
        this.#getSelectedFrequencyValue()
      );
    } else {
      /*
       * Achat unique :
       * aucun selling plan.
       */
      this.#updateSellingPlanInput('');
    }

    console.log(
      '[GLX Subscription] Mode :',
      optionType,
      '| selling_plan :',
      this.sellingPlanInput?.value || ''
    );

    this.#updateDisplayedPrices();

    if (notify) {
      this.dispatchEvent(
        new SubscriptionUpdatedEvent()
      );
    }
  }

  #updateSelectedCard(optionType) {
    this.oneTimeCard?.classList.remove(
      'subscription__option--selected'
    );

    this.subscriptionCard?.classList.remove(
      'subscription__option--selected'
    );

    if (
      optionType === 'subscription'
    ) {
      this.subscriptionCard?.classList.add(
        'subscription__option--selected'
      );
    } else {
      this.oneTimeCard?.classList.add(
        'subscription__option--selected'
      );
    }
  }

  /* =========================================
     SELLING PLAN
  ========================================= */

  #getSelectedFrequencyValue() {
    return (
      this.frequencySelect?.value ||
      ''
    );
  }

  #getSelectedFrequencyElement() {
    if (!this.frequencySelect) {
      return null;
    }

    return (
      this.frequencySelect.options[
        this.frequencySelect.selectedIndex
      ] ||
      null
    );
  }

  #restoreSellingPlan() {
    this.#updateSellingPlanInput(
      this.isSubscriptionSelected()
        ? this.#getSelectedFrequencyValue()
        : ''
    );
  }

  #updateSellingPlanInput(value) {
    if (!this.sellingPlanInput) {
      return;
    }

    this.sellingPlanInput.value =
      value || '';
  }

  /* =========================================
     REMISE DU SELLING PLAN
  ========================================= */

  #getSelectedPlanAdjustment() {
    const selectedElement =
      this.#getSelectedFrequencyElement();

    if (!selectedElement) {
      return null;
    }

    return {
      type:
        selectedElement.dataset
          .adjustmentType ||
        'percentage',

      value:
        parseFloat(
          selectedElement.dataset
            .adjustmentValue
        ) || 0
    };
  }

  #updateSavingBadge() {
    if (!this.savingBadge) return;

    const priceEl = this.subscriptionPriceContainer?.querySelector(
      '[data-ref="price"]'
    );

    const compareAtPriceEl = this.subscriptionPriceContainer?.querySelector(
      '[data-ref="compare-at-price"]'
    );

    const subscriptionPrice = Number(priceEl?.dataset.value);
    const compareAtPrice = Number(compareAtPriceEl?.dataset.value);

    if (
      !priceEl?.dataset.value ||
      !compareAtPriceEl?.dataset.value ||
      !Number.isFinite(subscriptionPrice) ||
      !Number.isFinite(compareAtPrice)
    ) {
      this.savingBadge.textContent = '';
      return;
    }

    const saving = Math.max(0, compareAtPrice - subscriptionPrice);

    this.savingBadge.textContent =
      saving > 0
        ? `Économisez ${formatMoney(saving)}`
        : '';
  }

  /* =========================================
     PRIX
  ========================================= */

  #getBasePrices() {
    const quantityBreaks =
      this.productForm?.querySelector(
        '[data-ref="quantity-breaks"]'
      );

    if (
      quantityBreaks &&
      typeof quantityBreaks.getPrices ===
        'function'
    ) {
      const prices =
        quantityBreaks.getPrices();

      const quantity =
        parseInt(
          quantityBreaks
            .getSelectedQuantity?.()
            ?.dataset?.quantity
        ) || 1;

      const discount =
        typeof quantityBreaks.getDiscount ===
          'function'
          ? quantityBreaks.getDiscount()
          : null;

      const priceAfterQB =
        parseInt(
          prices.price
        ) || 0;

      const compareAtPrice =
        parseInt(
          prices.compareAtPrice
        ) || priceAfterQB;

      /*
       * Cross-sells du quantity break
       */
      let crossSellPrice = 0;
      let crossSellCompareAtPrice = 0;

      if (
        typeof quantityBreaks
          .getCrossSellPrices ===
          'function'
      ) {
        const crossSellPrices =
          quantityBreaks.getCrossSellPrices();

        crossSellPrice =
          parseInt(
            crossSellPrices.price
          ) || 0;

        crossSellCompareAtPrice =
          parseInt(
            crossSellPrices.compareAtPrice
          ) || crossSellPrice;
      }

      const addToCartContainer =
        this.productForm?.querySelector(
          '[data-ref="add-to-cart-button-container"]'
        );

      const variantUnitPrice =
        parseInt(
          addToCartContainer
            ?.dataset
            ?.price
        ) || 0;

      const variantUnitCompareAtPrice =
        parseInt(
          addToCartContainer
            ?.dataset
            ?.compareAtPrice
        ) || variantUnitPrice;

      const priceBeforeQB =
        variantUnitPrice *
        quantity;

      return {
        priceAfterQB,

        priceBeforeQB,

        compareAtPrice:
          variantUnitCompareAtPrice *
          quantity,

        quantity,

        qbDiscount:
          discount,

        crossSellPrice,

        crossSellCompareAtPrice
      };
    }

    /*
     * Sans quantity break
     */
    const addToCartContainer =
      this.productForm?.querySelector(
        '[data-ref="add-to-cart-button-container"]'
      );

    const variantPrice =
      parseInt(
        addToCartContainer
          ?.dataset
          ?.price
      ) || 0;

    const variantCompareAtPrice =
      parseInt(
        addToCartContainer
          ?.dataset
          ?.compareAtPrice
      ) || variantPrice;

    return {
      priceAfterQB:
        variantPrice,

      priceBeforeQB:
        variantPrice,

      compareAtPrice:
        variantCompareAtPrice,

      quantity:
        1,

      qbDiscount:
        null,

      crossSellPrice:
        0,

      crossSellCompareAtPrice:
        0
    };
  }

  #updateDisplayedPrices() {
    const {
      priceAfterQB,
      priceBeforeQB,
      compareAtPrice,
      quantity,
      qbDiscount,
      crossSellPrice,
      crossSellCompareAtPrice
    } = this.#getBasePrices();

    if (
      priceAfterQB === null ||
      priceAfterQB === undefined
    ) {
      return;
    }

    /*
     * ACHAT UNIQUE
     */
    const oneTimeTotal =
      priceAfterQB +
      crossSellPrice;

    const oneTimeCompareAt =
      compareAtPrice +
      crossSellCompareAtPrice;

    this.#updatePriceElement(
      this.oneTimePriceContainer,
      oneTimeTotal,
      oneTimeCompareAt
    );

    /*
     * ABONNEMENT :
     *
     * 1. remise abonnement
     * 2. remise quantity break
     */
    const adjustment =
      this.#getSelectedPlanAdjustment();

    let subscriptionPrice =
      this.#applyPriceAdjustment(
        priceBeforeQB,
        adjustment,
        quantity
      );

    /*
    * Si Kaching utilise un PRIX FIXE,
    * adjustment.value correspond déjà au prix final
    * PAR ARTICLE.
    *
    * Exemple Pack 3 :
    * 23,30 € × 3 = 69,90 €
    *
    * On ne doit donc PAS appliquer ensuite
    * la réduction du Quantity Break.
    */
    if (
      adjustment?.type !== 'price' &&
      qbDiscount
    ) {
      if (
        qbDiscount.type === 'percentage' &&
        qbDiscount.percentage > 0
      ) {
        subscriptionPrice =
          Math.round(
            subscriptionPrice *
              (100 - qbDiscount.percentage) /
              100
          );
      } else if (qbDiscount.value > 0) {
        subscriptionPrice =
          Math.max(
            0,
            subscriptionPrice -
              qbDiscount.value
          );
      }
    }

    const subscriptionTotal =
      Math.max(
        0,
        subscriptionPrice
      ) +
      crossSellPrice;

    const subscriptionCompareAt =
      compareAtPrice +
      crossSellCompareAtPrice;

    this.#updatePriceElement(
      this.subscriptionPriceContainer,
      subscriptionTotal,
      subscriptionCompareAt
    );

    this.#updateSavingBadge();
  }

  #updatePriceElement(
    container,
    price,
    compareAtPrice
  ) {
    if (!container) return;

    const priceEl =
      container.querySelector(
        '[data-ref="price"]'
      );

    const compareAtPriceEl =
      container.querySelector(
        '[data-ref="compare-at-price"]'
      );

    if (priceEl) {
      priceEl.textContent =
        formatMoney(price);

      priceEl.dataset.value =
        price;
    }

    if (compareAtPriceEl) {
      if (
        compareAtPrice > price
      ) {
        compareAtPriceEl.textContent =
          formatMoney(
            compareAtPrice
          );

        compareAtPriceEl.dataset.value =
          compareAtPrice;
      } else {
        compareAtPriceEl.textContent = '';

        compareAtPriceEl.dataset.value = '';
      }
    }
  }

  /* =========================================
     API PUBLIQUE POUR product-form.js
  ========================================= */

  getSelectedSellingPlan() {
    this.#restoreSellingPlan();

    return (
      this.sellingPlanInput?.value ||
      ''
    );
  }

  isSubscriptionSelected() {
    return (
      this.subscriptionOption
        ?.checked ||
      false
    );
  }

  isSubscriptionActivated() {
    return (
      this.style.display !== 'none'
    );
  }

  getPriceAdjustment() {
    if (
      !this.isSubscriptionSelected()
    ) {
      return null;
    }

    return (
      this.#getSelectedPlanAdjustment()
    );
  }

  #applyPriceAdjustment(
    basePrice,
    adjustment,
    quantity = 1
  ) {
    if (!adjustment) {
      return basePrice;
    }

    switch (adjustment.type) {
      case 'percentage':
        return Math.round(
          basePrice *
            (
              100 -
              adjustment.value
            ) /
            100
        );

      case 'fixed_amount':
        return Math.max(
          0,
          basePrice -
            adjustment.value *
              quantity
        );

      case 'price':
        return (
          adjustment.value *
          quantity
        );

      default:
        return basePrice;
    }
  }
  getPlanAdjustmentForQuantity(quantity) {
    if (!this.frequencySelect) {
      return null;
    }

    /*
     * Toutes les fréquences d'un même pack ont
     * le même prix : on prend le plan du pack.
     */
    let matchingOption =
      this.#getPlansForPack(
        parseInt(quantity, 10)
      )[0]?.option;

    // Sécurité si aucun plan ne suit la convention de nom
    if (!matchingOption) {
      matchingOption = Array.from(
        this.frequencySelect.options
      ).find((option) => {
        const name = (
          option.dataset.planName ||
          option.textContent ||
          ''
        ).trim();

        const match = name.match(/\d+/);

        return (
          match &&
          parseInt(match[0], 10) ===
            parseInt(quantity, 10)
        );
      });
    }

    if (!matchingOption) {
      return null;
    }

    return {
      type:
        matchingOption.dataset.adjustmentType ||
        'percentage',

      value:
        parseFloat(
          matchingOption.dataset.adjustmentValue
        ) || 0
    };
  }

  calculateAdjustedPriceForQuantity(
    basePrice,
    quantity
  ) {
    const adjustment =
      this.getPlanAdjustmentForQuantity(quantity);

    return this.#applyPriceAdjustment(
      basePrice,
      adjustment,
      quantity
    );
  }
  calculateAdjustedPrice(
    basePrice,
    quantity = 1
  ) {
    return this.#applyPriceAdjustment(
      basePrice,
      this.getPriceAdjustment(),
      quantity
    );
  }
}
if (
  !customElements.get(
    'subscription-component'
  )
) {
  customElements.define(
    'subscription-component',
    SubscriptionComponent
  );
}
